"use client";
import {
  Checkbox,
  NumericInput,
  RadioButton,
  type RadioItem,
  Typography,
  useMediaQuery,
} from "@kyan/ui";
import { useDebounceValue } from "usehooks-ts";

import { defaultUnderlying } from "#/config/markets";
import { getOptionConstraints } from "#/config/orders";
import { tooltips } from "#/config/tooltips";
import { useValidateOptionSize } from "#/hooks/useValidateOptionSize";
import { useOption } from "#/store/options/tradePanel/option";
import { useOptionIsPostOnly } from "#/store/options/tradePanel/optionIsPostOnly";
import { useOptionLimitOrderType } from "#/store/options/tradePanel/optionLimitOrderType";
import { useOptionLimitPrice } from "#/store/options/tradePanel/optionLimitPrice";
import { useOptionSize } from "#/store/options/tradePanel/optionSize";
import { LimitOrder } from "#/types/orders";

import { FocusableSizeInput } from "../../../../../../../FocusableSizeInput/FocusableSizeInput";

const limitSubOrderTypes: RadioItem<LimitOrder>[] = [
  {
    id: LimitOrder.GTC,
    label: "GTC",
    className: "w-[54px]",
    tooltip: tooltips.GTC,
  },
  {
    id: LimitOrder.GTD,
    label: "GTD",
    className: "w-[54px]",
    tooltip: tooltips.GTD,
  },
];

export const OptionLimitOrder: React.FC = () => {
  const { option } = useOption();
  const { isSm } = useMediaQuery();
  const { optionSize, setOptionSize } = useOptionSize();
  const { optionLimitPrice, setOptionLimitPrice } = useOptionLimitPrice();
  const { optionLimitOrderType, setOptionLimitOrderType } =
    useOptionLimitOrderType();
  const { optionIsPostOnly, setOptionIsPostOnly } = useOptionIsPostOnly();

  // Placeholder logic before Options Updater:
  const [debouncedOptionSize] = useDebounceValue(optionSize, 800);

  const underlying = option?.underlying ?? defaultUnderlying;
  const { priceDisplayPrecisionNotional, sizeDisplayPrecision } =
    getOptionConstraints(underlying);

  const sizeError = useValidateOptionSize({
    size: debouncedOptionSize,
    underlying,
    isLimit: true,
  });

  const handleToggleSubOrderType = (id: LimitOrder) => {
    setOptionLimitOrderType(id);
  };

  return (
    <div className="flex w-full flex-col gap-2">
      <FocusableSizeInput
        inputProps={{
          rightIcon: "Qty",
          error: sizeError,
          value: optionSize,
          label: "Size in contracts",
          rightLabel: "Equity: 500 USDC",
          autoNumericOptions: {
            decimalPlaces: sizeDisplayPrecision,
          },
          onChange: setOptionSize,
        }}
      />
      <NumericInput
        rightIcon="USD"
        label="Limit price"
        placeholder="$0.00"
        variant="secondary"
        value={optionLimitPrice}
        className="ml-auto w-full"
        rightLabel="Equity: 500 USDC"
        wrapperClassName="h-11 sm:h-8"
        autoNumericOptions={{
          currencySymbol: "$",
          decimalPlaces: priceDisplayPrecisionNotional,
        }}
        onChange={setOptionLimitPrice}
      />

      <div className="border-divider mt-2.5 flex items-center justify-between border-b pb-2">
        <Typography grotesk primary size={12}>
          Limit order type
        </Typography>
        <div className="flex items-center gap-2">
          <Checkbox
            label="Post only"
            value={optionIsPostOnly}
            onValueChange={setOptionIsPostOnly}
          />
          <RadioButton
            variant="tertiary"
            size={isSm ? "sm" : "md"}
            items={limitSubOrderTypes}
            value={optionLimitOrderType}
            onChange={handleToggleSubOrderType}
          />
        </div>
      </div>
    </div>
  );
};
